import React, { useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import type { NodeProps } from '@xyflow/react';
import { Card, Typography, IconButton, Stack, Dropdown, Menu, MenuItem, MenuButton, Divider } from '@mui/joy';
import {
  Gear as SettingsIcon,
  DotsThreeVertical as MoreVertIcon,
  PencilSimple as EditIcon,
  Trash as DeleteIcon,
  ArrowsOutCardinal as MoveIcon,
  ArrowDown as MoveAllIcon
} from '@phosphor-icons/react';
import type { AutomationModifier } from '../../../types/automations';

interface ModifierNodeData {
  modifier: AutomationModifier;
  onEdit: () => void;
  onDelete: () => void;
  onMove?: () => void;
  onMoveAll?: () => void;
}

export default function ModifierNode({ data }: NodeProps) {
  const { modifier, onEdit, onDelete, onMove, onMoveAll } = data as unknown as ModifierNodeData;
  const [menuOpen, setMenuOpen] = useState(false);

  const getModifierLabel = (type: string) => {
    const labels: Record<string, string> = {
      wait: 'Wait',
      end: 'End Workflow',
      if_else: 'If/Else',
    };
    return labels[type] || type;
  };

  // End modifier has no output, if_else has two outputs, others have one
  const isEndModifier = modifier.modifier_type === 'end';
  const isIfElseModifier = modifier.modifier_type === 'if_else';

  return (
    <>
      <Handle type="target" position={Position.Top} />
      {/* If/Else gets two source handles for branching (true/false) */}
      {isIfElseModifier && (
        <>
          <Handle
            type="source"
            position={Position.Bottom}
            id="true"
            title="Yes branch"
            style={{ left: '30%', background: '#4caf50' }}
          />
          <Handle
            type="source"
            position={Position.Bottom}
            id="false"
            title="No branch"
            style={{ left: '70%', background: '#f44336' }}
          />
        </>
      )}
      {isIfElseModifier && <Handle type="source" position={Position.Bottom} id="continue" title="Continue after the branches" style={{ left: '50%' }} />}
      {/* Regular modifiers (wait) get a single source handle, end modifier gets none */}
      {!isIfElseModifier && !isEndModifier && (
        <Handle type="source" position={Position.Bottom} />
      )}
      <Card
        variant="outlined"
        sx={{
          minWidth: 250,
          bgcolor: 'warning.softBg',
          borderColor: 'warning.500',
          borderWidth: 2,
          cursor: 'default', // Prevent drag cursor
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <SettingsIcon color="var(--joy-palette-warning-500)" />
          <Typography level="title-md" sx={{ flex: 1 }}>
            {getModifierLabel(modifier.modifier_type)}
          </Typography>
          <Dropdown open={menuOpen} onOpenChange={(event, open) => setMenuOpen(open)}>
            <MenuButton
              slots={{ root: IconButton }}
              slotProps={{ root: { 
                size: 'sm', 
                variant: 'plain',
                onClick: (e: React.MouseEvent) => {
                  e.stopPropagation();
                }
              } }}
            >
              <MoreVertIcon size={18} />
            </MenuButton>
            <Menu placement="bottom-end">
              <MenuItem onClick={() => { onEdit(); setMenuOpen(false); }}>
                <EditIcon size={18} style={{ marginRight: 8 }} />
                Edit
              </MenuItem>
              {onMove && (
                <MenuItem onClick={() => { onMove(); setMenuOpen(false); }}>
                  <MoveIcon size={18} style={{ marginRight: 8 }} />
                  Move
                </MenuItem>
              )}
              {onMoveAll && (
                <MenuItem onClick={() => { onMoveAll(); setMenuOpen(false); }}>
                  <MoveAllIcon size={18} style={{ marginRight: 8 }} />
                  Move All From Here
                </MenuItem>
              )}
              <Divider />
              <MenuItem color="danger" onClick={() => { onDelete(); setMenuOpen(false); }}>
                <DeleteIcon size={18} style={{ marginRight: 8 }} />
                Delete
              </MenuItem>
            </Menu>
          </Dropdown>
        </Stack>
        {modifier.config && Object.keys(modifier.config).length > 0 && (
          <Typography level="body-sm" sx={{ mt: 1 }}>
            Configured
          </Typography>
        )}
      </Card>
    </>
  );
}
